import type { Answer, Chord, Level, Observatory } from '@astrasonora/annotation-schema'
import { cellOfAnswer, isStar } from '@astrasonora/annotation-schema'
import { cellName, isOpen, nightName } from './sky.ts'

/** What a quiz chord is judged against: the song's observatory and night. */
export interface AnswerContext {
  observatory: Observatory
  level: Level
}

/** The answer rule (spec §6): the cell, a root and a triad quality. The seventh is colour and is never asked. */
export function correct(a: Answer, chord: Chord): boolean {
  return cellOfAnswer(a) === chord.label.cell
}

/** Curation constraint (spec §6): may this chord be a quiz chord in the observatory at the night? */
export function quizAllowed(chord: Chord, ctx: AnswerContext): boolean {
  return quizBlockers(chord, ctx).length === 0
}

/**
 * Why a chord may not be quizzed (spec §6), for the curator and publish; empty when allowed. A quiz
 * chord is a plain major, minor or diminished triad on an open star, in root position, with or
 * without a seventh; sus and augmented chords and every extension the parser reduced to its triad
 * are labelled when they pass by, never asked.
 */
export function quizBlockers(chord: Chord, ctx: AnswerContext): string[] {
  const out: string[] = []
  const cell = chord.label.cell
  if (!isStar(cell)) out.push(`${cellName(cell)} is a dark cell: no night opens it`)
  else if (!isOpen(ctx.observatory, ctx.level, cell))
    out.push(`${cellName(cell)} is not open at ${nightName(ctx.level)} in this observatory`)
  const q = chord.abs.quality
  if (q === 'sus2' || q === 'sus4') out.push(`${q} is a voicing, never a quiz chord`)
  else if (q === 'aug') out.push('augmented chords are never quiz chords')
  if (chord.reduced === true)
    out.push('extension reduced to its triad (6, 9, add …); never a quiz chord')
  if (!chord.rootPosition) out.push('not in root position')
  return out
}

/** Smallest night at which every quiz chord is allowed in the observatory, or null if there is none. */
export function minLevel(chords: readonly Chord[], observatory: Observatory): Level | null {
  const quiz = chords.filter((c) => c.quiz)
  for (let level = 1 as Level; level <= 7; level = (level + 1) as Level)
    if (quiz.every((c) => quizAllowed(c, { observatory, level }))) return level
  return null
}
